// 자동 생성 — 원본은 packages/ui/binnie-ui.js
// Binnie 생성형 UI 렌더러 (단일 소스). 서버가 검증한 블록 스펙을 HTML 문자열로 그린다.
// 모든 문자열은 이스케이프한다 — 스펙에 무엇이 들어와도 스크립트/태그가 실행될 수 없다.
// 빌드: scripts/build-ui.mjs 가 이 파일을 apps/api/public/ui.js (전역 BinnieUI) 와 packages/widget/src/BinnieUI.js (ES 모듈) 로 복사한다.

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const safeUrl = (u) => (/^https?:\/\/[^\s<>"']+$/i.test(String(u || "")) ? String(u) : null);

// 인라인 마크다운: 굵게, 코드, 링크(http만), 줄바꿈
export function inline(md) {
  return esc(md)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, (_, t, u) => `<a href="${esc(u)}" target="_blank" rel="noopener noreferrer">${t}</a>`)
    .replace(/\n/g, "<br>");
}

// 여러 줄 마크다운(문단·불릿·번호)을 간단히
export function paragraphs(md) {
  const lines = String(md || "").split("\n");
  let html = "", i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (/^\s*[-*•]\s+/.test(line)) {
      let items = "";
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) items += `<li>${inline(lines[i++].replace(/^\s*[-*•]\s+/, ""))}</li>`;
      html += `<ul>${items}</ul>`; continue;
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      let items = "";
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) items += `<li>${inline(lines[i++].replace(/^\s*\d+[.)]\s+/, ""))}</li>`;
      html += `<ol>${items}</ol>`; continue;
    }
    const para = [line]; i++;
    while (i < lines.length && lines[i].trim() && !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) para.push(lines[i++]);
    html += `<p>${inline(para.join("\n"))}</p>`;
  }
  return html;
}

const tags = (list) => (list && list.length ? `<div class="bui-tags">${list.map((t) => `<span class="bui-tag">${esc(t)}</span>`).join("")}</div>` : "");

const RENDER = {
  heading: (b) => { const l = [1, 2, 3].includes(b.level) ? b.level : 2; return `<h${l} class="bui-h bui-h${l}">${inline(b.text)}</h${l}>`; },
  text: (b) => `<div class="bui-text">${paragraphs(b.md)}</div>`,
  stats: (b) => `<div class="bui-stats bui-cols-${Math.min(b.items.length, 4)}">${b.items.map((s) => `<div class="bui-stat"><div class="bui-stat-v">${inline(s.value)}</div><div class="bui-stat-l">${esc(s.label)}</div>${s.hint ? `<div class="bui-stat-h">${esc(s.hint)}</div>` : ""}</div>`).join("")}</div>`,
  cards: (b) => `<div class="bui-cards bui-cols-${b.columns || 2}">${b.items.map((c) => `<article class="bui-card">
      <div class="bui-card-head"><h3 class="bui-card-t">${inline(c.title)}</h3>${c.subtitle ? `<div class="bui-card-s">${esc(c.subtitle)}</div>` : ""}</div>
      ${c.body ? `<div class="bui-card-b">${paragraphs(c.body)}</div>` : ""}
      ${c.meta && c.meta.length ? `<dl class="bui-meta">${c.meta.map((m) => `<div><dt>${esc(m.label)}</dt><dd>${inline(m.value)}</dd></div>`).join("")}</dl>` : ""}
      ${tags(c.tags)}
      ${c.links && c.links.length ? `<div class="bui-links-row">${c.links.map((l) => safeUrl(l.url) ? `<a class="bui-btn" href="${esc(l.url)}" target="_blank" rel="noopener noreferrer">${esc(l.label)} ↗</a>` : "").join("")}</div>` : ""}
    </article>`).join("")}</div>`,
  table: (b) => `<div class="bui-tablewrap"><table class="bui-table">${b.caption ? `<caption>${esc(b.caption)}</caption>` : ""}<thead><tr>${b.columns.map((c) => `<th>${inline(c)}</th>`).join("")}</tr></thead><tbody>${b.rows.map((r) => `<tr>${b.columns.map((_, i) => `<td>${inline(r[i] ?? "")}</td>`).join("")}</tr>`).join("")}</tbody></table></div>`,
  timeline: (b) => `<ol class="bui-timeline">${b.items.map((t) => `<li class="bui-tl-item"><div class="bui-tl-date">${esc(t.date)}</div><div class="bui-tl-body"><div class="bui-tl-title">${inline(t.title)}</div>${t.body ? `<div class="bui-tl-text">${paragraphs(t.body)}</div>` : ""}${tags(t.tags)}</div></li>`).join("")}</ol>`,
  list: (b) => { const tag = b.style === "number" ? "ol" : "ul"; return `<${tag} class="bui-list bui-list-${b.style || "bullet"}">${b.items.map((i) => `<li>${inline(i)}</li>`).join("")}</${tag}>`; },
  chips: (b) => `<div class="bui-chips">${b.items.map((c) => `<span class="bui-chip bui-chip-${c.tone || "muted"}">${esc(c.text)}</span>`).join("")}</div>`,
  callout: (b) => `<div class="bui-callout bui-callout-${b.tone || "info"}">${paragraphs(b.md)}</div>`,
  progress: (b) => `<div class="bui-progress">${b.items.map((p) => `<div class="bui-prog"><div class="bui-prog-head"><span>${esc(p.label)}</span><span class="bui-prog-hint">${esc(p.hint || p.value + "%")}</span></div><div class="bui-prog-bar"><div class="bui-prog-fill" style="width:${Math.max(0, Math.min(100, Number(p.value) || 0))}%"></div></div></div>`).join("")}</div>`,
  compare: (b) => `<div class="bui-tablewrap"><table class="bui-table bui-compare"><thead><tr><th></th>${b.columns.map((c) => `<th>${inline(c)}</th>`).join("")}</tr></thead><tbody>${b.rows.map((r) => `<tr><th scope="row">${inline(r.label)}</th>${b.columns.map((_, i) => `<td>${inline(r.values[i] ?? "")}</td>`).join("")}</tr>`).join("")}</tbody></table></div>`,
  links: (b) => `<div class="bui-links">${b.items.map((l) => safeUrl(l.url) ? `<a class="bui-link" href="${esc(l.url)}" target="_blank" rel="noopener noreferrer"><span class="bui-link-l">${esc(l.label)} ↗</span>${l.description ? `<span class="bui-link-d">${esc(l.description)}</span>` : ""}</a>` : "").join("")}</div>`,
  divider: () => `<hr class="bui-divider">`,
  columns: (b) => `<div class="bui-columns bui-cols-${b.columns.length}">${b.columns.map((col) => `<div class="bui-col">${render(col)}</div>`).join("")}</div>`,
};

// 블록 배열(또는 블록 하나)을 HTML 로
export function render(blocks) {
  const list = Array.isArray(blocks) ? blocks : blocks ? [blocks] : [];
  return list.map((b) => (b && RENDER[b.type] ? `<div class="bui-block bui-${b.type}">${RENDER[b.type](b)}</div>` : "")).join("");
}

// 채팅 안 "페이지로 보기" 카드
export function pageCard(p) {
  const u = safeUrl(p.url) || "#";
  return `<a class="bui-pagecard" href="${esc(u)}" target="_blank" rel="noopener noreferrer">
    <div class="bui-pagecard-k">페이지로 보기</div>
    <div class="bui-pagecard-t">${esc(p.title || "Binnie 페이지")}</div>
    ${p.summary ? `<div class="bui-pagecard-s">${esc(p.summary)}</div>` : ""}
    <div class="bui-pagecard-a">새 탭에서 열기 ↗</div>
  </a>`;
}
